import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { rememberPostLoginPath } from '@/storage/post-login-redirect'
import { useAccountStore } from '@/stores/account-store'
import { LoadingScreen } from '@/components/common/loading-screen'
import { useGetUserQuery } from '@/services/api/users-api'
import { PostLoginNavigate } from './post-login-navigate'
import { SUBSCRIBED_BILLING_PATH, subscriptionState } from '../subscription-state'

/**
 * Protects the /billing (subscribe) page. Only users with NO usable subscription belong
 * here: active/grace users already have access (→ /dashboard), and a needs_fix user has a
 * subscription to REPAIR, not replace (→ /account/billing).
 */
export function BillingGuard({ children }: { children: ReactNode }) {
  const location = useLocation()
  const session = useAccountStore((s) => s.session)
  const isAuthenticating = useAccountStore((s) => s.isAuthenticating)

  // Also fetch user via RTK Query to ensure fresh data
  const { data: user, isLoading: userLoading } = useGetUserQuery(undefined, {
    skip: !session,
  })

  // Sync fetched user to store
  const setUser = useAccountStore((s) => s.setUser)
  useEffect(() => {
    if (user) setUser(user)
  }, [user, setUser])

  if (isAuthenticating) {
    return <LoadingScreen />
  }

  if (!session) {
    rememberPostLoginPath(location)
    return <Navigate to="/login" replace />
  }

  if (userLoading || !user) {
    return <LoadingScreen message="Loading account..." />
  }

  const state = subscriptionState(user, user.testClockTime ?? undefined)

  // active/grace already have access; needs_fix has a sub to repair, not replace.
  if (state === 'active' || state === 'grace') {
    return <PostLoginNavigate />
  }
  if (state === 'needs_fix') {
    return <Navigate to={SUBSCRIBED_BILLING_PATH} replace />
  }

  return <>{children}</>
}
